import React, { memo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Card, Row, Col, Avatar, Typography, Space, Switch, Button } from 'antd';
import { UserOutlined, SunOutlined, MoonOutlined } from '@ant-design/icons';
import { hapticFeedbackImpactOccurred } from '@telegram-apps/sdk-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';


const { Title, Text } = Typography;

const UserInfoCard = memo(({ user, isTelegramAvailable }) => {
  const switchRef = useRef(null);
  const navigate = useNavigate();
  const { currentTheme, updateUserTheme } = useApp();

  const toggleTheme = async (isDarkMode) => {
    const newTheme = isDarkMode ? 'dark' : 'light';
    hapticFeedbackImpactOccurred('light');
    
    /**
     * Return early if View Transition API is not supported
     * or user prefers reduced motion
     */
    if (
      !switchRef.current ||
      !document.startViewTransition ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      // Используем функцию из контекста для обновления темы с сохранением на сервере
      await updateUserTheme(newTheme);
      return;
    }

    await document.startViewTransition(() => {
      flushSync(() => {
        // Используем функцию из контекста для обновления темы с сохранением на сервере
        updateUserTheme(newTheme);
        // Синхронизируем CSS класс с темой для анимации
        document.documentElement.className = `theme-${newTheme}`;
      });
    }).ready;

    const { top, left, width, height } = switchRef.current.getBoundingClientRect();
    const x = left + width / 2;
    const y = top + height / 2;
    const right = window.innerWidth - left;
    const bottom = window.innerHeight - top;
    const maxRadius = Math.sqrt(
      Math.pow(Math.max(left, right), 2) +
      Math.pow(Math.max(top, bottom), 2),
    );

    document.documentElement.animate(
      {
        clipPath: [
          `circle(0px at ${x}px ${y}px)`,
          `circle(${maxRadius}px at ${x}px ${y}px)`,
        ],
      },
      {
        duration: 750,
        easing: 'ease-in-out',
        pseudoElement: '::view-transition-new(root)',
      }
    );
    
    // Обновляем цвета Telegram WebApp если доступен
    if (isTelegramAvailable && window.Telegram?.WebApp) {
      window.Telegram.WebApp.setHeaderColor(newTheme === 'dark' ? '#1d1d1d' : '#f5f5f5');
      window.Telegram.WebApp.setBackgroundColor(newTheme === 'dark' ? '#1d1d1d' : '#f5f5f5');
    }
  };

  const handleBalanceClick = () => {
    // Хаптик фидбек при нажатии на баланс
    if (isTelegramAvailable && window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
    }
    
    // Навигация на страницу реферальной программы
    navigate('/referral');
  };

  return (
    <Card>
      <Row align="middle" gutter={16}>
        <Col>
          <Avatar 
            size={96} 
            src={user.photo_url} 
            icon={<UserOutlined />}
          />
        </Col>
        <Col flex="auto">
          <Space direction="vertical" size={4}>
            <Title level={4} style={{ margin: 0 }}>
              {user.first_name}
            </Title>
            {user.username && <Text type="secondary">@{user.username}</Text>}
          </Space>
        </Col>
        <Col>
          <Space align="center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Space align="center">
              <SunOutlined style={{ color: currentTheme === 'light' ? "var(--tg-theme-button-color, #1677ff)" : '#bfbfbf' }} />
              <Switch
                ref={switchRef}
                checked={currentTheme === 'dark'}
                onChange={toggleTheme}
                size="middle"
                style={{
                  backgroundColor: currentTheme === 'dark' ? "var(--tg-theme-button-color, #1677ff)" : '#bfbfbf',
                }}
              />
              <MoonOutlined style={{ color: currentTheme === 'dark' ? "var(--tg-theme-button-color, #1677ff)" : '#bfbfbf' }} />
            </Space>
            <Button type="primary" onClick={handleBalanceClick}>
              <strong>{user.balance || 0} ₽</strong>
            </Button>
          </Space>
        </Col>
      </Row>
    </Card>
  );
});

UserInfoCard.displayName = 'UserInfoCard';

export default UserInfoCard; 